<template>
  <div class="cost-page">
    <div class="cost-container">
      <!-- Header -->
      <div class="page-header">
        <div class="header-content">
          <h1 class="page-title">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16">
              <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.604-.835-1.604-1.634 0-.92.724-1.6 1.85-1.738v3.372zm1.503 1.997c1.32.323 1.898.876 1.898 1.81 0 1.087-.826 1.853-2.125 1.969V8.94z"/>
            </svg>
            Breakdown des coûts
          </h1>
          <p v-if="breakdown && breakdown.dossier" class="page-subtitle">
            <span class="dossier-name">{{ breakdown.dossier.name || 'Sans titre' }}</span>
            <span v-if="breakdown.dossier.mode === 'AGENT'" class="agent-badge">✦ Agent</span>
            <span v-else-if="breakdown.dossier.mode === 'FLASH'" class="flash-badge">⚡ Flash</span>
            <span v-else-if="breakdown.dossier.mode" class="mode-badge">{{ breakdown.dossier.mode }}</span>
          </p>
          <p v-else class="page-subtitle">Analyse détaillée d'un dossier</p>
        </div>
        <router-link :to="{ name: 'adminDashboard' }" class="back-button">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path fill-rule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8"/>
          </svg>
          Retour au dashboard
        </router-link>
      </div>

      <!-- Search bar — jump to another dossier -->
      <div class="search-section">
        <div class="search-card">
          <div class="search-input-group">
            <input
              v-model="newDossierId"
              type="text"
              placeholder="Coller un autre UUID de dossier"
              class="search-input"
              @keyup.enter="goToOther"
            />
            <button @click="goToOther" :disabled="!newDossierId.trim() || loading" class="search-button">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
              </svg>
              Analyser
            </button>
          </div>
          <p class="search-hint" v-if="$route.params.dossierId">
            ID actuel : <code>{{ $route.params.dossierId }}</code>
            <span v-if="breakdown && breakdown.dossier && breakdown.dossier.user_email">
              · {{ breakdown.dossier.user_email }}
            </span>
            <span v-if="breakdown && breakdown.dossier && breakdown.dossier.created_at">
              · créé le {{ formatDateTime(breakdown.dossier.created_at) }}
            </span>
          </p>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="state-card loading-state">
        <div class="loading-spinner"></div>
        <p>Chargement du breakdown…</p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="state-card error-state">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" viewBox="0 0 16 16">
          <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5m.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2"/>
        </svg>
        <h3>{{ error }}</h3>
        <p v-if="error === 'Dossier introuvable'">Vérifie l'UUID et réessaie.</p>
      </div>

      <!-- Empty input state -->
      <div v-else-if="!$route.params.dossierId" class="state-card empty-state">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" viewBox="0 0 16 16">
          <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
        </svg>
        <p>Colle un UUID de dossier ci-dessus pour voir le breakdown.</p>
      </div>

      <!-- Tracking disabled (non-AGENT dossier) -->
      <div v-else-if="breakdown && !breakdown.tracking_enabled" class="state-card empty-state">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" viewBox="0 0 16 16">
          <path d="M2.5 3A1.5 1.5 0 0 0 1 4.5v.793c.026.009.051.02.076.032L7.674 8.51c.206.1.446.1.652 0l6.598-3.185A.755.755 0 0 1 15 5.293V4.5A1.5 1.5 0 0 0 13.5 3z"/>
          <path d="M15 6.954 8.978 9.86a2.25 2.25 0 0 1-1.956 0L1 6.954V11.5A1.5 1.5 0 0 0 2.5 13h11a1.5 1.5 0 0 0 1.5-1.5z"/>
        </svg>
        <h3>Pas de tracking détaillé sur ce dossier</h3>
        <p>
          Mode du dossier :
          <strong>{{ breakdown.dossier.mode || 'inconnu' }}</strong>.
          Le breakdown par étape n'est enregistré que pour les dossiers en mode <strong>AGENT</strong>.
        </p>
        <div v-if="breakdown.dossier.total_cost_usd != null" class="fallback-cost">
          Coût agrégé du dossier : <strong>{{ formatCost(breakdown.dossier.total_cost_usd) }}</strong>
        </div>
      </div>

      <!-- Main breakdown -->
      <div v-else-if="breakdown" class="breakdown-content">
        <!-- Stats cards -->
        <div class="stats-grid">
          <div class="stat-card stat-card-blue">
            <div class="stat-label">Coût total</div>
            <div class="stat-value">{{ formatCost(breakdown.totals.cost_usd) }}</div>
            <div class="stat-sub">{{ breakdown.totals.n_events }} appels LLM</div>
          </div>
          <div class="stat-card stat-card-green">
            <div class="stat-label">Tokens in</div>
            <div class="stat-value">{{ formatNumber(breakdown.totals.tokens_in) }}</div>
            <div class="stat-sub" v-if="breakdown.totals.tokens_cached">
              dont {{ formatNumber(breakdown.totals.tokens_cached) }} cachés
              ({{ Math.round((breakdown.totals.tokens_cached / breakdown.totals.tokens_in) * 100) }}%)
            </div>
          </div>
          <div class="stat-card stat-card-purple">
            <div class="stat-label">Tokens out</div>
            <div class="stat-value">{{ formatNumber(breakdown.totals.tokens_out) }}</div>
            <div class="stat-sub" v-if="breakdown.totals.tokens_reasoning">
              dont {{ formatNumber(breakdown.totals.tokens_reasoning) }} reasoning
            </div>
          </div>
          <div class="stat-card stat-card-orange">
            <div class="stat-label">Search units</div>
            <div class="stat-value">{{ formatNumber(breakdown.totals.search_units) }}</div>
            <div class="stat-sub">Cohere rerank</div>
          </div>
        </div>

        <!-- Breakdown par step -->
        <div class="section-container">
          <h3 class="section-heading">Répartition par étape</h3>
          <div class="table-container wide">
            <table class="data-table">
              <thead>
                <tr>
                  <th class="th-left">Étape</th>
                  <th class="th-num">Events</th>
                  <th class="th-num">In</th>
                  <th class="th-num">Cached</th>
                  <th class="th-num">Out</th>
                  <th class="th-num">Reasoning</th>
                  <th class="th-num">Search</th>
                  <th class="th-num">Cost</th>
                  <th class="th-bar">%</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in breakdown.by_step" :key="row.step" :class="idx % 2 === 0 ? 'row-even' : 'row-odd'">
                  <td class="td-left mono">{{ row.step }}</td>
                  <td class="td-num">{{ row.n_events }}</td>
                  <td class="td-num">{{ formatNumber(row.in_tok) }}</td>
                  <td class="td-num dim">{{ formatNumber(row.cached_tok) }}</td>
                  <td class="td-num">{{ formatNumber(row.out_tok) }}</td>
                  <td class="td-num dim">{{ formatNumber(row.reasoning_tok) }}</td>
                  <td class="td-num dim">{{ formatNumber(row.search_units) }}</td>
                  <td class="td-num cost">{{ formatCost(row.cost_usd) }}</td>
                  <td class="td-bar">
                    <div class="bar-track">
                      <div class="bar-fill-h" :style="{ width: row.pct + '%' }"></div>
                      <span class="bar-pct">{{ row.pct.toFixed(1) }}%</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Breakdown par modèle -->
        <div class="section-container">
          <h3 class="section-heading">Répartition par modèle</h3>
          <div class="table-container">
            <table class="data-table">
              <thead>
                <tr>
                  <th class="th-left">Provider</th>
                  <th class="th-left">Modèle</th>
                  <th class="th-num">Events</th>
                  <th class="th-num">In</th>
                  <th class="th-num">Out</th>
                  <th class="th-num">Cost</th>
                  <th class="th-bar">%</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in breakdown.by_model" :key="row.provider + row.model" :class="idx % 2 === 0 ? 'row-even' : 'row-odd'">
                  <td class="td-left">
                    <span class="provider-badge" :class="'provider-' + row.provider">{{ row.provider }}</span>
                  </td>
                  <td class="td-left mono">{{ row.model }}</td>
                  <td class="td-num">{{ row.n_events }}</td>
                  <td class="td-num">{{ formatNumber(row.in_tok) }}</td>
                  <td class="td-num">{{ formatNumber(row.out_tok) }}</td>
                  <td class="td-num cost">{{ formatCost(row.cost_usd) }}</td>
                  <td class="td-bar">
                    <div class="bar-track">
                      <div class="bar-fill-h" :style="{ width: row.pct + '%' }"></div>
                      <span class="bar-pct">{{ row.pct.toFixed(1) }}%</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Breakdown par source juridique -->
        <div class="section-container" v-if="breakdown.by_step_source && breakdown.by_step_source.length">
          <h3 class="section-heading">Ventilation par source juridique</h3>
          <div class="table-container">
            <table class="data-table">
              <thead>
                <tr>
                  <th class="th-left">Étape</th>
                  <th class="th-left">Source</th>
                  <th class="th-num">Events</th>
                  <th class="th-num">Cost</th>
                  <th class="th-bar">%</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in breakdown.by_step_source" :key="row.step + '-' + row.source" :class="idx % 2 === 0 ? 'row-even' : 'row-odd'">
                  <td class="td-left mono">{{ row.step }}</td>
                  <td class="td-left">
                    <span class="source-badge">{{ row.source }}</span>
                  </td>
                  <td class="td-num">{{ row.n_events }}</td>
                  <td class="td-num cost">{{ formatCost(row.cost_usd) }}</td>
                  <td class="td-bar">
                    <div class="bar-track">
                      <div class="bar-fill-h" :style="{ width: row.pct + '%' }"></div>
                      <span class="bar-pct">{{ row.pct.toFixed(1) }}%</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Boucle ReAct par iteration -->
        <div class="section-container" v-if="iterationsForChart.length">
          <h3 class="section-heading">Coût par itération de la boucle ReAct</h3>
          <div class="iter-chart">
            <div v-for="row in iterationsForChart" :key="row.iteration" class="iter-bar">
              <div class="iter-bar-container">
                <div class="iter-bar-fill" :style="{ height: ((row.cost_usd / maxIterationCost) * 100) + '%' }"></div>
              </div>
              <div class="iter-bar-label">
                <div class="iter-num">#{{ row.iteration }}</div>
                <div class="iter-cost">{{ formatCost(row.cost_usd) }}</div>
                <div class="iter-events">{{ row.n_events }} ev</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sub agents (workspace) -->
        <div class="section-container" v-if="breakdown.by_sub_agent && breakdown.by_sub_agent.length">
          <h3 class="section-heading">Sub-agents (workspace)</h3>
          <div class="table-container">
            <table class="data-table">
              <thead>
                <tr>
                  <th class="th-left">Sub-agent</th>
                  <th class="th-num">Events</th>
                  <th class="th-num">Cost</th>
                  <th class="th-bar">%</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in breakdown.by_sub_agent" :key="row.sub_agent" :class="idx % 2 === 0 ? 'row-even' : 'row-odd'">
                  <td class="td-left mono">{{ row.sub_agent }}</td>
                  <td class="td-num">{{ row.n_events }}</td>
                  <td class="td-num cost">{{ formatCost(row.cost_usd) }}</td>
                  <td class="td-bar">
                    <div class="bar-track">
                      <div class="bar-fill-h" :style="{ width: row.pct + '%' }"></div>
                      <span class="bar-pct">{{ row.pct.toFixed(1) }}%</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Top 10 events -->
        <div class="section-container">
          <h3 class="section-heading collapsible" @click="showTopEvents = !showTopEvents">
            Top 10 events les plus chers
            <span class="chevron">{{ showTopEvents ? '▾' : '▸' }}</span>
          </h3>
          <div v-if="showTopEvents" class="table-container">
            <table class="data-table">
              <thead>
                <tr>
                  <th class="th-left">Étape</th>
                  <th class="th-left">Modèle</th>
                  <th class="th-num">Iter</th>
                  <th class="th-num">In</th>
                  <th class="th-num">Out</th>
                  <th class="th-num">Cost</th>
                  <th class="th-num">Latence</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in breakdown.top_events" :key="row.id" :class="idx % 2 === 0 ? 'row-even' : 'row-odd'">
                  <td class="td-left mono">
                    {{ row.step }}<span v-if="row.source" class="dim"> · {{ row.source }}</span>
                  </td>
                  <td class="td-left mono">{{ row.model }}</td>
                  <td class="td-num">{{ row.iteration != null ? row.iteration : '—' }}</td>
                  <td class="td-num">{{ formatNumber(row.input_tokens) }}</td>
                  <td class="td-num">{{ formatNumber(row.output_tokens) }}</td>
                  <td class="td-num cost">{{ formatCost(row.cost_usd) }}</td>
                  <td class="td-num dim">{{ row.latency_ms ? row.latency_ms + 'ms' : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Cross-check footer -->
        <div class="footer-note">
          Cross-check : somme des coûts par étape = <strong>{{ formatCost(breakdown.totals.cost_usd) }}</strong>
          <span v-if="breakdown.dossier.total_cost_usd != null">
            · <code>dossiers.total_cost_usd</code> = {{ formatCost(breakdown.dossier.total_cost_usd) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { supabase } from "@/supabase/supabaseClient";

export default {
  name: 'DossierCostBreakdown',
  data() {
    return {
      loading: false,
      breakdown: null,
      error: null,
      adminPassword: null,
      newDossierId: '',
      showTopEvents: false,
    };
  },
  computed: {
    iterationsForChart() {
      if (!this.breakdown || !this.breakdown.by_iteration) return [];
      return this.breakdown.by_iteration.filter(r => r.iteration != null);
    },
    maxIterationCost() {
      const max = Math.max(...this.iterationsForChart.map(r => r.cost_usd));
      return max > 0 ? max : 1;
    },
  },
  watch: {
    '$route.params.dossierId'(newId) {
      if (newId) {
        this.fetchBreakdown(newId);
      } else {
        this.breakdown = null;
        this.error = null;
      }
    },
  },
  methods: {
    promptForPassword() {
      if (!this.adminPassword) {
        const password = prompt('Mot de passe administrateur requis :');
        if (!password) {
          throw new Error('Mot de passe requis pour accéder aux données administrateur');
        }
        this.adminPassword = password;
      }
      return this.adminPassword;
    },

    isValidUuid(id) {
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test((id || '').trim());
    },

    goToOther() {
      const id = (this.newDossierId || '').trim();
      if (!id) return;
      if (!this.isValidUuid(id)) {
        this.$toast.error("UUID invalide");
        return;
      }
      this.newDossierId = '';
      if (id !== this.$route.params.dossierId) {
        this.$router.push({ name: 'dossierCostBreakdown', params: { dossierId: id } });
      } else {
        this.fetchBreakdown(id);
      }
    },

    async fetchBreakdown(dossierId) {
      if (!dossierId) return;
      if (!this.isValidUuid(dossierId)) {
        this.error = "UUID invalide";
        return;
      }

      this.loading = true;
      this.error = null;
      this.breakdown = null;

      try {
        const { data } = await supabase.auth.getSession();
        const jwt = data?.session?.access_token;
        if (!jwt) throw new Error('Session expirée');

        const adminPassword = this.promptForPassword();

        const response = await fetch('https://api.query-juriste.com/copilot/admin/get-dossier-cost-breakdown', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${jwt}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            dossier_id: dossierId,
            admin_password: adminPassword,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          if (response.status === 403) this.adminPassword = null;
          if (response.status === 404) {
            this.error = "Dossier introuvable";
            return;
          }
          throw new Error(errorData.error || `Erreur ${response.status}`);
        }

        this.breakdown = await response.json();
      } catch (err) {
        console.error('Error fetching cost breakdown:', err);
        this.error = err.message || 'Erreur lors du chargement du breakdown';
        this.$toast?.error(this.error);
      } finally {
        this.loading = false;
      }
    },

    formatCost(value) {
      if (value == null) return '—';
      const v = Number(value);
      if (isNaN(v)) return '—';
      if (v < 0.01) return '$' + v.toFixed(4);
      if (v < 1) return '$' + v.toFixed(3);
      return '$' + v.toFixed(2);
    },

    formatNumber(value) {
      if (value == null) return '—';
      const v = Number(value);
      if (isNaN(v)) return '—';
      return v.toLocaleString('fr-FR');
    },

    formatDateTime(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
  mounted() {
    const id = this.$route.params.dossierId;
    if (id) this.fetchBreakdown(id);
  },
};
</script>

<style scoped>
.cost-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem 1rem;
}

.cost-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  color: white;
  gap: 1rem;
  flex-wrap: wrap;
}

.header-content {
  flex: 1;
  min-width: 300px;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 2.25rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.page-subtitle {
  font-size: 1rem;
  opacity: 0.95;
  margin: 0;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.dossier-name {
  font-weight: 500;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 12px 20px;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-2px);
}

/* Search */
.search-section {
  margin-bottom: 2rem;
}

.search-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 1.5rem 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.search-input-group {
  display: flex;
  gap: 1rem;
}

.search-input {
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  transition: border-color 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: #667eea;
}

.search-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 12px 20px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.search-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.search-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.search-hint {
  margin: 0.75rem 0 0 0;
  font-size: 0.85rem;
  color: #4a5568;
}

.search-hint code {
  background: #edf2f7;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.8rem;
}

/* States */
.state-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 3rem 2rem;
  text-align: center;
  color: #4a5568;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.state-card svg {
  opacity: 0.5;
  margin-bottom: 1rem;
}

.state-card h3 {
  margin: 0.5rem 0;
  font-size: 1.25rem;
  color: #2d3748;
}

.state-card p {
  margin: 0.25rem 0;
  font-size: 0.95rem;
}

.error-state svg {
  color: #e53e3e;
  opacity: 0.6;
}

.fallback-cost {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f7fafc;
  border-radius: 8px;
  display: inline-block;
  font-size: 1rem;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(102, 126, 234, 0.2);
  border-radius: 50%;
  border-top: 3px solid #667eea;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Stats grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat-card {
  padding: 1.5rem;
  border-radius: 12px;
  border: 2px solid;
}

.stat-card-blue {
  background: linear-gradient(135deg, #ebf4ff 0%, #c3dafe 100%);
  border-color: #90cdf4;
}

.stat-card-green {
  background: linear-gradient(135deg, #f0fff4 0%, #c6f6d5 100%);
  border-color: #9ae6b4;
}

.stat-card-purple {
  background: linear-gradient(135deg, #faf5ff 0%, #e9d8fd 100%);
  border-color: #d6bcfa;
}

.stat-card-orange {
  background: linear-gradient(135deg, #fffaf0 0%, #feebc8 100%);
  border-color: #fbd38d;
}

.stat-label {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
  opacity: 0.8;
  color: #2d3748;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: #1a202c;
  font-variant-numeric: tabular-nums;
}

.stat-sub {
  font-size: 0.8rem;
  color: #4a5568;
  margin-top: 0.25rem;
  font-weight: 500;
}

/* Sections */
.breakdown-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.section-container {
  background: rgba(255, 255, 255, 0.97);
  border-radius: 16px;
  padding: 1.75rem 2rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.section-heading {
  margin: 0 0 1.25rem 0;
  font-size: 1.15rem;
  font-weight: 600;
  color: #2d3748;
}

.section-heading.collapsible {
  cursor: pointer;
  user-select: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chevron {
  font-size: 0.9rem;
  color: #667eea;
  font-weight: 700;
}

/* Tables */
.table-container {
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  overflow: auto;
  max-height: 500px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.data-table thead {
  position: sticky;
  top: 0;
  background: #edf2f7;
  z-index: 1;
}

.data-table th {
  padding: 0.75rem 1rem;
  font-weight: 600;
  color: #4a5568;
  border-bottom: 2px solid #e2e8f0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.75rem;
  white-space: nowrap;
}

.th-left { text-align: left; }
.th-num { text-align: right; }
.th-bar { text-align: left; min-width: 130px; }

.data-table td {
  padding: 0.75rem 1rem;
  vertical-align: middle;
}

.td-left { text-align: left; }
.td-num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.td-bar { padding-right: 1rem; }

.cost {
  font-weight: 600;
  color: #2d3748;
}

.dim { color: #a0aec0; }

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.8rem;
}

.row-even { background: white; }
.row-odd { background: #f7fafc; }

/* Bar % */
.bar-track {
  position: relative;
  height: 22px;
  background: #edf2f7;
  border-radius: 11px;
  overflow: hidden;
  min-width: 100px;
}

.bar-fill-h {
  height: 100%;
  background: linear-gradient(90deg, #667eea, #764ba2);
  border-radius: 11px;
  transition: width 0.4s ease;
}

.bar-pct {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 600;
  color: #2d3748;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.5);
}

/* Provider badges */
.provider-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.provider-openai { background: #d1fae5; color: #065f46; }
.provider-google { background: #dbeafe; color: #1e40af; }
.provider-cohere { background: #fef3c7; color: #92400e; }
.provider-unknown { background: #edf2f7; color: #4a5568; }

.source-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  background: #ede9fe;
  color: #6d28d9;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  letter-spacing: 0.3px;
}

/* Iteration chart */
.iter-chart {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  height: 180px;
  padding: 1rem 0;
  overflow-x: auto;
}

.iter-bar {
  flex: 1;
  min-width: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  height: 100%;
}

.iter-bar-container {
  flex: 1;
  width: 100%;
  background: #f7fafc;
  border-radius: 6px;
  display: flex;
  align-items: flex-end;
}

.iter-bar-fill {
  width: 100%;
  background: linear-gradient(180deg, #667eea, #764ba2);
  border-radius: 6px;
  min-height: 4px;
  transition: height 0.4s ease;
}

.iter-bar-label {
  text-align: center;
  font-size: 0.7rem;
}

.iter-num {
  font-weight: 700;
  color: #2d3748;
}

.iter-cost {
  color: #667eea;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

.iter-events {
  color: #a0aec0;
}

/* Footer */
.footer-note {
  margin-top: 0.5rem;
  padding: 1rem 1.5rem;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 12px;
  font-size: 0.85rem;
  color: #4a5568;
  text-align: center;
}

.footer-note code {
  background: #edf2f7;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.8rem;
}

/* Mode badges */
.flash-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  color: #854d0e;
  font-size: 0.65rem;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid rgba(255, 255, 255, 0.4);
}

.agent-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
  color: #6d28d9;
  font-size: 0.65rem;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid rgba(124, 58, 237, 0.3);
}

.mode-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 0.65rem;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

/* Responsive */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .page-title {
    font-size: 1.75rem;
  }

  .search-input-group {
    flex-direction: column;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .section-container {
    padding: 1.25rem;
  }
}
</style>
